import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Search, Edit, Trash2, Building2, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { Departamento } from '../../types';
import { DepartamentosService } from '../../services';
import DepartmentModal from '../../components/ui/DepartmentModal';
import ConfirmationModal from '../../components/ConfirmationModal';

const Departments: React.FC = () => {
  const { usuario } = useAuth();
  const [departments, setDepartments] = useState<Departamento[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState({
    status: 'all'
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedDepartment, setSelectedDepartment] = useState<Departamento | null>(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    departmentId: 0,
    departmentName: ''
  });

  useEffect(() => {
    if (usuario) {
      loadDepartments();
    }
  }, [usuario]);

  const loadDepartments = async () => {
    try {
      setIsLoading(true);
      const response = await DepartamentosService.getDepartamentos();
      setDepartments(response || []);
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
      toast.error('Erro ao carregar departamentos');
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteModal = (departmentId: number, departmentName: string) => {
    setDeleteModal({
      isOpen: true,
      departmentId,
      departmentName
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      departmentId: 0,
      departmentName: ''
    });
  };

  const confirmDelete = async () => {
    try {
      await DepartamentosService.deleteDepartamento(deleteModal.departmentId);
      toast.success('Departamento excluído com sucesso');
      loadDepartments(); // Recarregar lista
      closeDeleteModal();
    } catch (error: any) {
      console.error('Erro ao excluir departamento:', error);
      if (error.message?.includes('Acesso negado')) {
        toast.error('Você não tem permissão para excluir este departamento');
      } else {
        toast.error('Erro ao excluir departamento');
      }
    }
  };

  const handleSaveDepartment = async (data: Partial<Departamento>): Promise<void> => {
    try {
      if (modalMode === 'create') {
        await DepartamentosService.createDepartamento(data);
        toast.success('Departamento criado com sucesso!');
      } else {
        if (selectedDepartment) {
          await DepartamentosService.updateDepartamento(selectedDepartment.id, data);
          toast.success('Departamento atualizado com sucesso!');
        }
      }
      loadDepartments(); // Recarregar lista
    } catch (error: any) {
      console.error('Erro ao salvar departamento:', error);
      toast.error(error.message || 'Erro ao salvar departamento');
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedDepartment(null);
    setIsModalOpen(true);
  };

  const openEditModal = (department: Departamento) => {
    setModalMode('edit');
    setSelectedDepartment(department);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDepartment(null);
  };

  const filteredDepartments = departments.filter(dept => {
    const matchesSearch = dept.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filters.status === 'all' || 
                         (filters.status === 'active' && dept.ativo === 1) ||
                         (filters.status === 'inactive' && dept.ativo === 0);
    
    return matchesSearch && matchesStatus;
  });

  // Verificar se o usuário tem acesso
  if (!usuario || usuario.nivel !== 'dev') {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 dark:text-gray-600 text-6xl mb-4">🚫</div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Acesso Negado</h3>
        <p className="text-gray-500 dark:text-gray-400">Apenas desenvolvedores podem acessar esta página.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Departamentos
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Gerencie os departamentos da empresa
            </p>
            <div className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Total de departamentos: {departments.length} • Filtrados: {filteredDepartments.length}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={openCreateModal}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Departamento
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Buscar departamentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      </div>

      {/* Departments Table */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Usuários
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tickets
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Data de Criação
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredDepartments.length > 0 ? (
                filteredDepartments.map((dept) => (
                  <tr key={dept.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="p-1.5 sm:p-2 rounded-lg bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 mr-2 sm:mr-3">
                          <Building2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                          {dept.nome || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        dept.ativo === 1 ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                      }`}>
                        {dept.ativo === 1 ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
                          {dept.total_usuarios || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300">
                          {dept.total_tickets || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        {dept.created_at ? new Date(dept.created_at).toLocaleDateString('pt-BR') : 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => openEditModal(dept)}
                          className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-300 p-1"
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(dept.id, dept.nome || '')}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1"
                          title="Excluir departamento"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-3 sm:px-6 py-8 sm:py-12 text-center text-gray-500 dark:text-gray-400">
                    <Building2 className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-base sm:text-lg font-medium">Nenhum departamento encontrado</p>
                    <p className="text-xs sm:text-sm">Tente ajustar os filtros de busca ou crie um novo departamento</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para criar/editar departamentos */}
      <DepartmentModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSave={handleSaveDepartment}
        department={selectedDepartment}
        mode={modalMode}
      />

      {/* Modal de Confirmação de Exclusão */}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isOpen={deleteModal.isOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          title="Confirmar Exclusão"
          message={`Tem certeza que deseja excluir o departamento "${deleteModal.departmentName}"? Esta ação não pode ser desfeita.`}
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      )}
    </div>
  );
};

export default Departments;
